import { Box, Flex } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useMountedState } from 'react-use';
import { Waypoint } from 'react-waypoint';
import clientApi from '~/clientApi';
import Button from '~/components/Button';
import LinkButton from '~/components/LinkButton';
import PostPreviewCard from '~/components/PostPreviewCard';
import {
  ModalNoResults,
  postSortingOptions as sortingOptions,
} from '~/components/RecentContentFiltersBar';
import Spinner from '~/components/Spinner';
import { CONTENT_TYPES, MEDIA_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import { CONTENTS_COLLECTION_QUERY_KEY } from '~/queries/keys';
import { selectContentModalTypes } from '~/types/content';
import { pluralize } from '~/utils';

import { Modal } from '../Modal';
import {
  DesktopModalFiltersBar as ModalFiltersBar,
  OpenFiltersButton,
} from '../RecentContentFiltersBar';
import { SelectContentGrid } from '../SelectContentGrid';
import { SelectedContentPreview } from '../SelectedContentPreview';

const API_DATE_FORMAT = 'YYYY-MM-DD';
const PAGE_SIZE = 15;

const propTypes = {
  ...selectContentModalTypes,
  postTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(MEDIA_TYPES))),
  resultsFilter: PropTypes.func,
  titles: PropTypes.shape({
    modal: PropTypes.node,
    selectedPreview: PropTypes.node,
  }),
  creatorId: PropTypes.string,
};

const fetchPosts = async (query, creatorId) => {
  const { results } = await clientApi.content.index({
    ...query,
    _impersonateOnce: creatorId,
  });

  return results;
};

const fetchInitialContent = (ids, creatorId) => {
  const promises = ids.map((id) =>
    clientApi.content.read({ id, _impersonateOnce: creatorId }),
  );

  return Promise.all(promises);
};

const defaultTitles = {
  modal: 'Choose your Instagram Post Highlights',
  selectedPreview: 'Selected highlights:',
};

/* This is the Modal from new MP
 *
 * TODO: It is necessary to refactor this part and
 * change to typescript all Select for the all Social Media
 * and the components related with this part.
 */
export const SelectPostsModal = ({
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Save Changes',
  disabledItemIds = [],
  fetchContentFn = fetchPosts,
  fetchInitialContentFn = fetchInitialContent,
  initialSelectedItemIds = [],
  isOpen = false,
  maxItems = 6,
  minItems = 3,
  onCancel,
  onClose,
  onSubmit,
  postTypes = null,
  resultsFilter = (i) => i,
  shouldShowSelectionPreview,
  titles: titlesProp = {},
  creatorId,
}) => {
  const titles = { ...defaultTitles, ...titlesProp };
  const [sortBy, setSortBy] = React.useState(sortingOptions[0]);
  const [adFilter, setAdFilter] = React.useState(false);
  const [tags, setTags] = React.useState([]);
  const [startDate, setStartDate] = React.useState();
  const [endDate, setEndDate] = React.useState();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isFiltersBoxVisible, setIsFiltersBoxVisible] = React.useState(false);
  const isMounted = useMountedState();
  const [selectedItems, setSelectedItems] = React.useState(
    initialSelectedItemIds,
  );
  const [selectedContent, setSelectedContent] = React.useState([]);

  const filtersApplied =
    !!adFilter || tags.length > 0 || !!startDate || !!endDate;

  const queryParams = {
    channel: 'post',
    pageSize: PAGE_SIZE,
    type: postTypes,
    order: sortBy?.value,
    hashtag: adFilter ? 'on' : undefined,
    query: tags.length > 0 ? tags.join(',') : undefined,
    'postedAt[after]': startDate
      ? startDate.format(API_DATE_FORMAT)
      : undefined,
    'postedAt[before]': endDate
      ? // If we don't send a time, the BE will consider T00:00:00 by default
        `${endDate.format(API_DATE_FORMAT)}T23:59:59`
      : undefined,
  };

  const {
    status,
    data = {
      pages: [],
    },
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    [CONTENTS_COLLECTION_QUERY_KEY, queryParams],
    ({ pageParam: page = 1 }) =>
      fetchContentFn({ ...queryParams, page }, creatorId),
    {
      getNextPageParam: (lastGroup, allGroups) =>
        lastGroup?.length >= PAGE_SIZE && allGroups.length + 1,
      enabled: isOpen,
    },
  );

  const posts = data.pages.flat().filter(resultsFilter);

  const initialStringified =
    !!initialSelectedItemIds.length && JSON.stringify(initialSelectedItemIds);
  const selectedStringified = JSON.stringify(selectedItems);

  // Fetch the contents of the items initially selected.
  // This is used to show the selected contents preview up top,
  // since they might not be fetched yet due to pagination
  React.useEffect(() => {
    if (initialStringified) {
      // eslint-disable-next-line no-inner-declarations
      async function fetchData() {
        const response = await fetchInitialContentFn(
          initialSelectedItemIds,
          creatorId,
        );

        setSelectedContent(response);
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStringified]);

  // Update the list of selected contents whenever the selected items change.
  React.useEffect(() => {
    setSelectedContent(
      selectedItems
        .map((id) => {
          // If the selected item is already visible on the posts list, return that content
          // else return it from the already selected contents
          return (
            posts.find((post) => post.id === id) ||
            selectedContent.find((c) => c.id === id)
          );
        })
        .filter((item) => !!item),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStringified]);

  React.useEffect(() => {
    if (initialSelectedItemIds) {
      setSelectedItems(initialSelectedItemIds);
    }
  }, [initialSelectedItemIds]);

  const handleSubmitClick = async () => {
    setIsSubmitting(true);
    await onSubmit(
      selectedContent,
      CONTENT_TYPES.post,
      SOCIAL_PLATFORMS.instagram,
    );

    if (isMounted()) {
      setSelectedItems(initialSelectedItemIds);
      setIsSubmitting(false);
    }
  };

  const count = selectedItems.length;

  const isSubmitDisabled = count < minItems || count > maxItems;
  const previewError =
    count < minItems
      ? `You have to select at least ${minItems} ${pluralize(
          'post',
          'posts',
          minItems,
        )}`
      : undefined;
  const resetSelectedItems = () => setSelectedItems(initialSelectedItemIds);

  const handleClose = () => {
    resetSelectedItems();
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      resetSelectedItems();
      onCancel();
    } else {
      handleClose();
    }
  };

  const handleDateChange = (newValues) => {
    setStartDate(newValues.startDate);
    setEndDate(newValues.endDate);
  };

  const handleClearFilters = () => {
    setAdFilter(false);
    setTags([]);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const filtersBarProps = {
    sortingOptions: sortingOptions,
    sortByValue: sortBy,
    onSortByChange: setSortBy,
    adFilterValue: adFilter,
    onAdFilterChange: setAdFilter,
    tagsValue: tags,
    onTagsChange: setTags,
    startDate: startDate,
    endDate: endDate,
    onDatesChange: handleDateChange,
  };

  const handleItemClick = (id) => {
    let updatedItems;

    if (!selectedItems.includes(id)) {
      // adding
      if (selectedItems.length >= maxItems) {
        // do nothing if max reached
        return false;
      } else {
        updatedItems = [...selectedItems, id];
      }
    } else {
      //removing
      updatedItems = selectedItems.filter((selected) => selected !== id);
    }

    // update
    setSelectedItems(updatedItems);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isFullScreenOnMobile>
      <Modal.Header title={titles.modal} />
      {shouldShowSelectionPreview && (
        <SelectedContentPreview
          contentType={CONTENT_TYPES.post}
          error={previewError}
          maxItems={maxItems}
          minItems={minItems}
          onChange={handleItemClick}
          selectedContent={selectedContent}
          title={titles.selectedPreview}
        />
      )}
      <Flex alignItems="center" justifyContent="space-between">
        <Box as="span" id="select-content-grid-hint">{`Choose ${pluralize(
          'one',
          'up to %n',
          maxItems,
        )}:`}</Box>

        <OpenFiltersButton
          hasFilterSet={filtersApplied}
          isFiltersBoxVisible={isFiltersBoxVisible}
          onClearFilters={handleClearFilters}
          onClick={() => setIsFiltersBoxVisible(!isFiltersBoxVisible)}
        />
      </Flex>

      {isFiltersBoxVisible && <ModalFiltersBar {...filtersBarProps} />}
      <Modal.Body marginTop={{ base: 4, md: 6 }} overflowY="auto">
        <SelectContentGrid
          aria-describedby="select-content-grid-hint"
          aria-label="eligible posts"
          columns={{ base: 3, md: 5, lg: 6 }}
          disabledItems={disabledItemIds}
          isDisabled={count === maxItems}
          max={maxItems}
          selectedItems={selectedItems}
        >
          {posts.map((item) => (
            <SelectContentGrid.Item
              key={item.id}
              id={item.id}
              borderRadius="lg"
              overflow="hidden"
              onClick={handleItemClick}
            >
              <PostPreviewCard post={item} borderRadius="lg" />
            </SelectContentGrid.Item>
          ))}
        </SelectContentGrid>
        {(status === 'loading' || isFetchingNextPage) && <Spinner centered />}
        {status === 'success' && filtersApplied && posts.length === 0 && (
          <ModalNoResults />
        )}
        {hasNextPage && (
          <Waypoint
            bottomOffset={-10}
            key={hasNextPage}
            onEnter={() => fetchNextPage()}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <LinkButton fontSize="md" colorScheme="primary" onClick={handleCancel}>
          {cancelButtonText}
        </LinkButton>
        <Button
          colorScheme="secondary"
          isDisabled={isSubmitDisabled}
          isLoading={isSubmitting}
          onClick={handleSubmitClick}
        >
          {confirmButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SelectPostsModal.propTypes = propTypes;
